import { IAssetDTO } from '../models/IAssetDTO';
import { EngineConstants } from './EngineConstants';
import { AssetsClient } from '@shared/frontend';
import { useEffect, useState } from 'react';

/** Anomalous Firebase Auth error codes: system/abuse conditions worth a Warn. Any other `auth/*` code is a routine credential/validation rejection (expected user input) and logs at Debug. */
const anomalousAuthErrorCodes: string[] = [EngineConstants.AuthErrorNetworkRequestFailed, EngineConstants.AuthErrorInternalError, EngineConstants.AuthErrorTooManyRequests];

/**
 * Classifies a Firebase Auth error code as an EXPECTED user-input failure (wrong password, unknown user, duplicate email, weak password, etc.) versus an anomalous system/abuse condition. Callers
 * log expected failures at Debug (routine, not actionable) and anomalous ones at Warn. A non-`auth/*` code is treated as unexpected (not an expected auth rejection).
 *
 * @param code - The error code from `Logger.GetErrorCode(err)`.
 * @returns True when the failure is a routine, expected auth rejection.
 */
export function IsExpectedAuthError(code: string): boolean {
  return code.startsWith(EngineConstants.AuthErrorPrefix) && !anomalousAuthErrorCodes.includes(code);
}

/** File-local constants for Helpers. */
const localConstants = {
  imageMimeType: 'image/jpeg',
  canvasContext2D: '2d'
} as const;

/**
 * Async hook that resolves an asset DTO to a loadable URL via AssetsClient.
 *
 * @param assetDTO - The asset descriptor, or null.
 * @returns The resolved URL, or null while loading.
 */
export function useAssetUrl(assetDTO: IAssetDTO | null): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!assetDTO) return;
    const controller = new AbortController();
    (async () => {
      const uri = await AssetsClient.GetAssetURI(assetDTO.manifest, assetDTO.bundle, assetDTO.asset);
      if (!controller.signal.aborted) {
        setUrl(uri ?? null);
      }
    })();
    return () => controller.abort();
  }, [assetDTO]);

  return url;
}

/**
 * Checks if an image is loaded successfully.
 *
 * @param src - The source URL of the image.
 * @returns A promise that resolves to true if the image loads successfully, otherwise false.
 */
export function ImageLoadedCheck(src: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = document.createElement('img');
    const done = (success: boolean) => resolve(success);
    const decode = () => {
      img
        .decode()
        .then(() => done(true))
        .catch(() => done(false));
    };

    img.onload = decode;
    img.onerror = () => done(false);
    img.src = src;

    if (img.complete) {
      decode();
    }
  });
}

/**
 * Resizes an image file to fit within maxSize and returns a JPEG data URL.
 *
 * @param file - The image file to resize.
 * @param maxSize - The maximum width/height in pixels.
 * @param quality - The JPEG quality (0-1).
 * @returns A promise that resolves to the resized image as a data URL.
 */
export function ResizeImageFile(file: File, maxSize: number, quality: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = document.createElement('img');
    img.onload = () => {
      const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.getContext(localConstants.canvasContext2D)!.drawImage(img, 0, 0, w, h);
      const dataUrl = canvas.toDataURL(localConstants.imageMimeType, quality);
      URL.revokeObjectURL(img.src);
      resolve(dataUrl);
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image'));
    };
    img.src = URL.createObjectURL(file);
  });
}
